import React from 'react';
import styles from './BetTableABJ.module.css';

const BetTableABJ = ({ data = [], exposures = {}, min = "10.0", max = "100K" }) => {
  
  // Get exposure value
  const getExposure = (selection) => {
    const exposureKeys = [
      selection,
      selection?.toLowerCase(),
      selection?.trim()
    ];

    let exposure = 0;
    for (const key of exposureKeys) {
      if (key && exposures[key] !== undefined) {
        exposure = exposures[key];
        break;
      }
    }
    return exposure;
  };

  // Check if bet is suspended
  const isSuspended = (betData) => {
    if (!betData) return true;
    const value = betData?.b ?? 0;
    const gstatus = betData?.gstatus?.toUpperCase();
    return value === 0 || value === "0" || value === undefined || value === null || gstatus === "SUSPENDED";
  };

  // Render bet cell
  const renderBetCell = (betData, selection, cellType = 'sa', hasYellowBorder = false) => {
    const suspended = isSuspended(betData);
    const value = betData?.b || 0;
    const exposure = getExposure(selection);
    const displayExposure = exposure !== 0 ? (exposure < 0 ? exposure.toFixed(0) : `+${exposure.toFixed(0)}`) : "0";

    return (
      <>
        <div 
          className={`${suspended ? styles.suspended : ''} ${hasYellowBorder && !suspended ? styles.activeBorder : ''}`}
          style={{ position: 'relative' }}
        >
          <div>{selection}</div>
          <div className={styles.mt1}>{value}</div>
        </div>
        <div className={styles.book} style={{ color: exposure !== 0 ? (exposure < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
          {displayExposure}
        </div>
      </>
    );
  };

  // Get suit icon path
  const getSuitIcon = (suitName) => {
    const suitMap = {
      'Spade': '/assets/img/cardIcons/spade.png',
      'Club': '/assets/img/cardIcons/club.png',
      'Heart': '/assets/img/cardIcons/heart.png',
      'Diamond': '/assets/img/cardIcons/diamond.png'
    };
    return suitMap[suitName] || '/assets/img/cardIcons/spade.png';
  };

  // Extract data from API response
  // sid 1: SA (Andar)
  // sid 2: 1st Bet (Andar)
  // sid 3: 2nd Bet (Andar)
  // sid 4: SB (Bahar)
  // sid 5: 1st Bet (Bahar)
  // sid 6: 2nd Bet (Bahar)
  // sid 7-19: Joker A through Joker K
  // sid 20-23: Joker suits (Spade, Club, Heart, Diamond)
  // sid 24-25: Joker Odd/Even

  const andarData = {
    sa: data.find(item => item.sid === 1) || {},
    firstBet: data.find(item => item.sid === 2) || {},
    secondBet: data.find(item => item.sid === 3) || {}
  };

  const baharData = {
    sb: data.find(item => item.sid === 4) || {},
    firstBet: data.find(item => item.sid === 5) || {},
    secondBet: data.find(item => item.sid === 6) || {}
  };

  // Determine which bet has yellow border for each section (only one active bet per section)
  const getActiveBetWithBorder = (bet1, bet2, bet3) => {
    if (!isSuspended(bet1) && bet1?.b > 0) return 'bet1';
    if (!isSuspended(bet2) && bet2?.b > 0) return 'bet2';
    if (!isSuspended(bet3) && bet3?.b > 0) return 'bet3';
    return null;
  };

  const andarActiveBet = getActiveBetWithBorder(andarData.sa, andarData.firstBet, andarData.secondBet);
  const baharActiveBet = getActiveBetWithBorder(baharData.sb, baharData.firstBet, baharData.secondBet);

  const jokerCards = data.filter(item => item.sid >= 7 && item.sid <= 19);
  const jokerSuits = [
    data.find(item => item.sid === 20) || {}, // Spade
    data.find(item => item.sid === 21) || {}, // Club
    data.find(item => item.sid === 22) || {}, // Heart
    data.find(item => item.sid === 23) || {}  // Diamond
  ];
  const jokerOdd = data.find(item => item.sid === 24) || {};
  const jokerEven = data.find(item => item.sid === 25) || {};

  return (
    <div className={styles.casinoContainer}>
      <div className={styles.ab2Container}>
        {/* Andar Section */}
        <div className={styles.betA}>
          <div className={styles.aTitle}>A</div>
          <div className={styles.sa}>
            {renderBetCell(andarData.sa, "SA", "sa", andarActiveBet === 'bet1')}
          </div>
          <div className={styles.firstBet}>
            {renderBetCell(andarData.firstBet, "1st Bet", "firstBet", andarActiveBet === 'bet2')}
          </div>
          <div className={styles.secondBet}>
            {renderBetCell(andarData.secondBet, "2nd Bet", "secondBet", andarActiveBet === 'bet3')}
          </div>
          <div className={styles.aTitle}>A</div>
        </div>

        {/* Bahar Section */}
        <div className={styles.betA}>
          <div className={styles.aTitle}>B</div>
          <div className={styles.sa}>
            {renderBetCell(baharData.sb, "SB", "sa", baharActiveBet === 'bet1')}
          </div>
          <div className={styles.firstBet}>
            {renderBetCell(baharData.firstBet, "1st Bet", "firstBet", baharActiveBet === 'bet2')}
          </div>
          <div className={styles.secondBet}>
            {renderBetCell(baharData.secondBet, "2nd Bet", "secondBet", baharActiveBet === 'bet3')}
          </div>
          <div className={styles.aTitle}>B</div>
        </div>
      </div>

      {/* Odd/Even Section */}
      <div className={`${styles.ab2Box} ${styles.mt2}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className={styles.col6}>
            <div className={styles.bltitle}>
              <b>Odd</b>
            </div>
            <div className={`${styles.back} ${styles.mt1} ${styles.blbox} ${isSuspended(jokerOdd) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
              <span className={styles.odd}>{jokerOdd?.b || 0}</span>
            </div>
            <div className={styles.mt1}>
              <span style={{ color: getExposure("Joker Odd") !== 0 ? (getExposure("Joker Odd") < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
                {getExposure("Joker Odd") !== 0 ? (getExposure("Joker Odd") < 0 ? getExposure("Joker Odd").toFixed(0) : `+${getExposure("Joker Odd").toFixed(0)}`) : "0"}
              </span>
            </div>
          </div>
          <div className={styles.col6}>
            <div className={styles.bltitle}>
              <b>Even</b>
            </div>
            <div className={`${styles.back} ${styles.mt1} ${styles.blbox} ${isSuspended(jokerEven) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
              <span className={styles.odd}>{jokerEven?.b || 0}</span>
            </div>
            <div className={styles.mt1}>
              <span style={{ color: getExposure("Joker Even") !== 0 ? (getExposure("Joker Even") < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
                {getExposure("Joker Even") !== 0 ? (getExposure("Joker Even") < 0 ? getExposure("Joker Even").toFixed(0) : `+${getExposure("Joker Even").toFixed(0)}`) : "0"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Suits Section */}
      <div className={`${styles.ab2Box} ${styles.mt2}`}>
        <div className={styles.row5}>
          <div className={styles.col3}>
            <div className={styles.bltitle}>
              <img src={getSuitIcon('Spade')} alt="Spade" onError={(e) => e.target.style.display = 'none'} />
            </div>
            <div className={`${styles.back} ${styles.mt1} ${styles.blbox} ${isSuspended(jokerSuits[0]) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
              <span className={styles.odd}>{jokerSuits[0]?.b || 0}</span>
            </div>
            <div className={styles.mt1}>
              <span style={{ color: getExposure("Joker Spade") !== 0 ? (getExposure("Joker Spade") < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
                {getExposure("Joker Spade") !== 0 ? (getExposure("Joker Spade") < 0 ? getExposure("Joker Spade").toFixed(0) : `+${getExposure("Joker Spade").toFixed(0)}`) : "0"}
              </span>
            </div>
          </div>
          <div className={styles.col3}>
            <div className={styles.bltitle}>
              <img src={getSuitIcon('Club')} alt="Club" onError={(e) => e.target.style.display = 'none'} />
            </div>
            <div className={`${styles.back} ${styles.mt1} ${styles.blbox} ${isSuspended(jokerSuits[1]) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
              <span className={styles.odd}>{jokerSuits[1]?.b || 0}</span>
            </div>
            <div className={styles.mt1}>
              <span style={{ color: getExposure("Joker Club") !== 0 ? (getExposure("Joker Club") < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
                {getExposure("Joker Club") !== 0 ? (getExposure("Joker Club") < 0 ? getExposure("Joker Club").toFixed(0) : `+${getExposure("Joker Club").toFixed(0)}`) : "0"}
              </span>
            </div>
          </div>
          <div className={styles.col3}>
            <div className={styles.bltitle}>
              <img src={getSuitIcon('Heart')} alt="Heart" onError={(e) => e.target.style.display = 'none'} />
            </div>
            <div className={`${styles.back} ${styles.mt1} ${styles.blbox} ${isSuspended(jokerSuits[2]) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
              <span className={styles.odd}>{jokerSuits[2]?.b || 0}</span>
            </div>
            <div className={styles.mt1}>
              <span style={{ color: getExposure("Joker Heart") !== 0 ? (getExposure("Joker Heart") < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
                {getExposure("Joker Heart") !== 0 ? (getExposure("Joker Heart") < 0 ? getExposure("Joker Heart").toFixed(0) : `+${getExposure("Joker Heart").toFixed(0)}`) : "0"}
              </span>
            </div>
          </div>
          <div className={styles.col3}>
            <div className={styles.bltitle}>
              <img src={getSuitIcon('Diamond')} alt="Diamond" onError={(e) => e.target.style.display = 'none'} />
            </div>
            <div className={`${styles.back} ${styles.mt1} ${styles.blbox} ${isSuspended(jokerSuits[3]) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
              <span className={styles.odd}>{jokerSuits[3]?.b || 0}</span>
            </div>
            <div className={styles.mt1}>
              <span style={{ color: getExposure("Joker Diamond") !== 0 ? (getExposure("Joker Diamond") < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
                {getExposure("Joker Diamond") !== 0 ? (getExposure("Joker Diamond") < 0 ? getExposure("Joker Diamond").toFixed(0) : `+${getExposure("Joker Diamond").toFixed(0)}`) : "0"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Joker Cards Section */}
      <div className={`${styles.ab2Box} ${styles.mt2}`}>
        <div className={styles.row5}>
          <div className={styles.col12}>
            <div className={styles.textCenter}>0</div>
          </div>
        </div>
        <div className={`${styles.row5} ${styles.mt1}`}>
          <div className={styles.col12}>
            <div className={styles.textCenter}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((cardNum) => {
                const jokerCard = jokerCards.find(item => {
                  const nat = item?.nat || '';
                  const cardMap = {
                    'Joker A': 1, 'Joker 2': 2, 'Joker 3': 3, 'Joker 4': 4, 'Joker 5': 5,
                    'Joker 6': 6, 'Joker 7': 7, 'Joker 8': 8, 'Joker 9': 9, 'Joker 10': 10,
                    'Joker J': 11, 'Joker Q': 12, 'Joker K': 13
                  };
                  return cardMap[nat] === cardNum;
                }) || {};
                const suspended = isSuspended(jokerCard);
                const exposure = getExposure(jokerCard?.nat || `Joker ${cardNum}`);
                const displayExposure = exposure !== 0 ? (exposure < 0 ? exposure.toFixed(0) : `+${exposure.toFixed(0)}`) : "0";

                return (
                  <div 
                    key={cardNum} 
                    className={styles.cardImage}
                    style={{ position: 'relative' }}
                  >
                    <div className={suspended ? styles.suspended : ''}>
                      <img 
                        src={`https://d1arlbwbznybm5.cloudfront.net/v19/static/front/images/andar_bahar/${cardNum}.jpg`} 
                        alt={`Card ${cardNum}`}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                    <div className={`${styles.ubook} ${styles.textCenter} ${styles.mt5}`}>
                      <b style={{ color: exposure !== 0 ? (exposure < 0 ? '#ff0000' : '#00ff00') : 'black' }}>
                        {displayExposure}
                      </b>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetTableABJ;

